import React, { useEffect, useState } from "react";
import Header from "../components/header";
import Footer from "../components/Footer";
import BackToTop from "../components/BackToTop";
import WhatsappButton from "../components/WhatsappButton";
import { useDarkMode } from "../Functions/DarkMode";
import { Helmet } from "react-helmet-async";
import { getAllProperties, propertiesToArray } from "../Functions/FetchProperties";

const About = () => {
    const { darkMode } = useDarkMode();
    const [propertyCount, setPropertyCount] = useState(0);
    
    // Add effect to load the CSS file
    useEffect(() => {
        // Load the CSS file for this page
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = '/assets/css/About.css';
        document.head.appendChild(link);
        
        // Clean up by removing the CSS when component unmounts
        return () => {
            if (document.head.contains(link)) {
                document.head.removeChild(link);
            }
        };
    }, []);

    // Fetch property count when component mounts
    useEffect(() => {
        const fetchPropertyCount = async () => {
            try {
                const propertiesData = await getAllProperties();
                const propertiesArray = propertiesToArray(propertiesData);
                setPropertyCount(propertiesArray.length);
            } catch (error) {
                console.error("Error fetching property count:", error);
            }
        };
        
        fetchPropertyCount();
    }, []);

    // Apply theme to document body to ensure consistent styling
    useEffect(() => {
        // Apply dark mode to body element for consistent background color
        if (darkMode) {
            document.body.setAttribute('data-theme', 'dark');
        } else {
            document.body.removeAttribute('data-theme');
        }
    }, [darkMode]);

    return (
        <div className="lsq-site-container">
            <Helmet>
                <title>About LSQ Stays | Our Story, Mission & Team</title>
                <meta name="description" content="Learn about LSQ Stays, our mission to provide luxury vacation experiences, and meet the team behind our exceptional property offerings." />
                <link rel="canonical" href="https://www.lsqstays.in/about" />
                <script type="application/ld+json">
                {JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "AboutPage",
                    "mainEntity": {
                        "@type": "Organization",
                        "name": "LSQ Stays",
                        "description": "LSQ Stays is a leading provider of luxurious vacation rentals designed for the discerning traveler.",
                        "url": "https://www.lsqstays.in",
                        "logo": {
                            "@type": "ImageObject",
                            "url": "https://www.lsqstays.in/assets/Images/logo.png",
                            "width": 112,
                            "height": 112,
                            "contentUrl": "https://www.lsqstays.in/assets/Images/logo.png"
                        },
                        "image": "https://www.lsqstays.in/assets/Images/logo.png",
                        "foundingDate": "2025",
                        "founders": [
                            {
                                "@type": "Person",
                                "name": "Kunal Mendon",
                                "jobTitle": "Founder"
                            }
                        ]
                    }
                })}
                </script>
            </Helmet>
            <Header currentPage="about" />
            
            <div className="about-page">
                <div className="about-content">
                    <div className="container">
                        <header className="about-header">
                            <h1>About LSQ Stays</h1>
                            <p>LSQ Stays is a leading provider of luxurious vacation rentals designed for the discerning traveler. Whether you're looking for a quiet getaway or an adventurous retreat, we offer villas in some of the most beautiful locations to give you the ultimate experience.</p>
                            
                            <div className="stats">
                                <div className="stat-item">
                                    <span className="stat-icon">🏠</span>
                                    <span>{propertyCount}+ Properties Listed</span>
                                </div>
                                <div className="stat-item">
                                    <span className="stat-icon">😊</span>
                                    <span>50K+ Happy Guests</span>
                                </div>
                                <div className="stat-item">
                                    <span className="stat-icon">📸</span>
                                    <span>20K+ Followers</span>
                                </div>
                            </div>
                        </header>
                        
                        <section className="gallery">
                            <img src="/assets/Images/exp.webp" alt="Luxury villa interior" className="gallery-img" />
                            <img src="/assets/Images/exp2.webp" alt="Aerial view of overwater villas" className="gallery-img" />
                        </section>
                        
                        <section className="team">
                            <h2>Our Founder</h2>
                            <div className="team-content">
                                <div className="team-member">
                                    <img src="/assets/Images/kunal.jpg" alt="" className="team-img" />
                                    <div className="team-info">
                                        <h3>Kunal Mendon</h3>
                                        <p>Founder</p>
                                    </div>
                                </div>
                                
                                <div className="article-preview">
                                    <img 
                                        src="https://www.inc91.com/uploads/images/202109/image_750x_613c85598d67b.jpg" 
                                        alt="Kunal Mendon - LSQ Group Founder" 
                                        className="article-img" 
                                    />
                                    <h3>Kunal Mendon : The founder of Isq Group of resorts and Premium Villas says: Without proper opportunity, it is tough to reach up to the desired success.</h3>
                                    <div className="article-meta">
                                        <span>Source: Inc91.com</span>
                                        <span className="article-date">Published: 2021</span>
                                    </div>
                                    <div className="article-content">
                                        <p>With the success of LSQ rooms, Kunal Mendon has gone one step forward to set up Tulip Stays. Tulip Stays has started its first Stay in Karjat called Rivergate. With a vast open area for play and a big swimming pool, this project assures you a wonderful time spent for the entire day. This is probably one of our best resorts.</p>
                                        <p>Under his leadership, LSQ Group has expanded its portfolio to include some of the most sought-after properties, each delivering unparalleled comfort and personalized service.</p>
                                        <a href="https://www.inc91.com/kunal-mendon-the-founder-of-isq-group-of-resorts-and-premium-villas" target="_blank" rel="noopener noreferrer" className="read-more-btn">
                                            Read Full Article <span className="arrow">→</span>
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </section>
                    </div>
                </div> 
                
                <Footer />
                <BackToTop />
                <WhatsappButton message="Hello, I'd like to know more about LSQ Stays" />
            </div>
        </div>
    );
}

export default About;